// Task-board types for the client dashboard. Tasks now come from the canonical
// coaching plan (dashboard.plan.list); meetings and coach comments are still
// mock-only until their backends land.

export type TaskType =
  | 'leetcode'
  | 'system-design'
  | 'video'
  | 'article'
  | 'exercise'
  | 'meeting'
  | 'task'

export type PeerLevel = 'beginner' | 'intermediate' | 'advanced'

/** Meeting metadata attached to homework tasks of type 'meeting' */
export interface TaskMeeting {
  meetingId: string
  kind: 'accountability' | 'peer'
  /** Display time, e.g. "8:00 PM" */
  time: string
  role?: 'interviewer' | 'interviewee'
  peerName?: string
  peerLevel?: PeerLevel
  suggestedQuestion?: {
    title: string
    url: string
    solutionUrl: string
  }
}

export type Difficulty = 'Easy' | 'Medium' | 'Hard'

export interface TaskLink {
  label: string
  url: string
}

/** The specific part of a solution a coach comment addresses */
export type CommentAspect =
  | 'complexity'
  | 'code'
  | 'post-mortem'
  | 'duration'
  | 'general'

export const COMMENT_ASPECT_LABELS: Record<CommentAspect, string> = {
  complexity: 'complexity analysis',
  code: 'code',
  'post-mortem': 'post-mortem',
  duration: 'pacing',
  general: 'solution',
}

export interface CoachComment {
  id: string
  author: string
  text: string
  aspect: CommentAspect
  date: string // ISO date string
}

export interface HomeworkTask {
  id: string
  title: string
  type: TaskType
  date: string // yyyy-MM-dd
  description: string
  /** The coach's single note for this assignment, written before or after the work. */
  coachNote?: string | null
  /** Catalog-level notes every client assigned this item sees. */
  universalNotes?: string | null
  difficulty?: Difficulty
  links: TaskLink[]
  /** Platform-tracked tasks (submission-review assignments) auto-complete; not user-toggleable */
  platformTracked: boolean
  completed: boolean
  comments: CoachComment[]
  commentsRead: boolean
  meeting?: TaskMeeting
  /** Canonical assignment optimistic-concurrency version, echoed on status commands */
  assignmentVersion?: number
  completionMode?: 'manual' | 'submission_review'
}

const WEEKDAY_ABBREVIATIONS = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
] as const

/** Weekday column headers for a seven-column month grid, Monday first. */
export function weekdayHeaders(): string[] {
  return [...WEEKDAY_ABBREVIATIONS]
}

export interface Sprint {
  number: number
  /** yyyy-MM-dd of the day this sprint starts, on the engagement's anchor */
  startDate: string
  /** yyyy-MM-dd of the day this sprint ends, on the engagement's anchor */
  endDate: string
  theme: string
}
